'use client'

import {useMemo, useState} from 'react'
import {Lightbox, ZoomIcon, type LightboxImage} from './Lightbox'
import {RichText} from './RichText'
import type {RichTextValue} from '@/lib/richtext'

export type CaseImage = {thumb: string; full: string; alt: string; caption?: string | null}
export type CaseStudyItem = {
  id: string
  title: string
  description?: RichTextValue
  images: CaseImage[]
}

export function CaseStudyGallery({cases}: {cases: CaseStudyItem[]}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  // Flatten every case image into one gallery; remember each image's global index.
  const {lightboxImages, startIndex} = useMemo(() => {
    const flat: LightboxImage[] = []
    const starts: number[] = []
    cases.forEach((c) => {
      starts.push(flat.length)
      c.images.forEach((im) => {
        flat.push({
          full: im.full,
          alt: im.alt,
          caption: [c.title, im.caption].filter(Boolean).join(' — '),
        })
      })
    })
    return {lightboxImages: flat, startIndex: starts}
  }, [cases])

  return (
    <div className="ba-cases">
      {cases.map((c, ci) => (
        <article className="ba-case" key={c.id}>
          <h2>{c.title}</h2>
          <RichText value={c.description} />
          {c.images.length > 0 && (
            <div className="ba-case-imgs">
              {c.images.map((im, i) => (
                <button
                  type="button"
                  className="ba-case-fig"
                  key={i}
                  onClick={() => setOpenIndex(startIndex[ci] + i)}
                  aria-label={`View larger image — ${c.title}${im.caption ? ` (${im.caption})` : ''}`}
                >
                  <span className="ba-case-thumb-wrap">
                    <img className="ba-case-img" src={im.thumb} alt={im.alt} loading="lazy" />
                    <span className="ba-case-zoom" aria-hidden="true">
                      <ZoomIcon />
                    </span>
                  </span>
                  {im.caption && <span className="ba-case-cap">{im.caption}</span>}
                </button>
              ))}
            </div>
          )}
        </article>
      ))}

      <Lightbox images={lightboxImages} openIndex={openIndex} setOpenIndex={setOpenIndex} />
    </div>
  )
}
